import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { Store, Inbox, Settings as SettingsIcon, LogOut, CreditCard, Mail, Plug, ShieldCheck } from "lucide-react";
import LanguagePicker from "@/components/LanguagePicker";
import { api } from "@/lib/api";

const PLAN_COLORS = { starter: "text-blue-600 bg-blue-50", business: "text-green-700 bg-green-50", agency: "text-purple-700 bg-purple-50" };

function UsageBar({ used, limit, label }) {
    const pct = limit <= 0 ? 0 : Math.min(100, Math.round((used / limit) * 100));
    const atLimit = limit > 0 && used >= limit;
    return (
        <div className="space-y-0.5">
            <div className="flex justify-between text-xs">
                <span className="text-gray-500">{label}</span>
                <span className={atLimit ? "text-red-600 font-semibold" : "text-gray-700 font-mono"}>
                    {limit <= 0 ? "∞" : `${used}/${limit}`}
                </span>
            </div>
            {limit > 0 && (
                <div className="h-1 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-1 rounded-full transition-all ${atLimit ? "bg-red-500" : "bg-[#0b905d]"}`} style={{ width: `${pct}%` }} />
                </div>
            )}
        </div>
    );
}

function PlanUsageSummary({ subscription, t }) {
    const planId = subscription?.plan_id;
    const planName = planId ? (planId.charAt(0).toUpperCase() + planId.slice(1)) : t("billing_plan_none");
    const colorClass = PLAN_COLORS[planId] || "text-gray-600 bg-gray-100";
    const limits = subscription?.limits || {};
    const usage = subscription?.usage || {};
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colorClass}`}>{planName}</span>
            </div>
            <UsageBar used={usage.shops || 0} limit={limits.shops ?? 1} label={t("billing_usage_shops")} />
            {(limits.withdrawals_per_month ?? -1) !== -1 && (
                <UsageBar used={usage.withdrawals_this_month || 0} limit={limits.withdrawals_per_month} label={t("billing_usage_withdrawals")} />
            )}
        </div>
    );
}

export default function DashboardLayout() {
    const { merchant, logout } = useAuth();
    const { t, lang, setLang } = useI18n();
    const nav = useNavigate();
    const [stats, setStats] = useState({ new: 0, in_progress: 0, done: 0, total: 0, shops: 0 });
    const [subscription, setSubscription] = useState(null);

    useEffect(() => {
        api.get("/stats/summary").then(({ data }) => setStats(data)).catch(() => {});
        api.get("/billing/me").then(({ data }) => setSubscription(data)).catch(() => {});
    }, []);

    const onLogout = () => {
        logout();
        nav("/login");
    };

    return (
        <div className="dashboard-shell">
            <aside className="dashboard-sidebar">
                <Link to="/app/withdrawals" className="flex items-center gap-2 mb-8" data-testid="sidebar-logo">
                    <div className="h-8 w-8 rounded-md bg-[#0b905d] flex items-center justify-center">
                        <span className="text-white font-display font-bold">W</span>
                    </div>
                    <span className="font-display font-bold text-[#00153f]">
                        widerruf<span className="text-[#0b905d] text-xs font-semibold">.online</span>
                    </span>
                </Link>

                <nav className="flex flex-col gap-1">
                    <NavLink to="/app/shops" data-testid="nav-shops" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
                        <Store className="w-4 h-4" />
                        <span>{t("nav_shops")}</span>
                        <span className="ml-auto font-mono text-xs opacity-70">{stats.shops}</span>
                    </NavLink>
                    <NavLink to="/app/withdrawals" data-testid="nav-withdrawals" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
                        <Inbox className="w-4 h-4" />
                        <span>{t("nav_withdrawals")}</span>
                        <span className="ml-auto font-mono text-xs opacity-70">{stats.total}</span>
                    </NavLink>
                    <NavLink to="/app/settings" data-testid="nav-settings" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
                        <SettingsIcon className="w-4 h-4" />
                        <span>{t("nav_settings")}</span>
                    </NavLink>
                    <NavLink to="/app/email" data-testid="nav-email" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
                        <Mail className="w-4 h-4" />
                        <span>{t("nav_email")}</span>
                    </NavLink>
                    <NavLink to="/app/integrations" data-testid="nav-integrations" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
                        <Plug className="w-4 h-4" />
                        <span>{t("nav_integrations")}</span>
                    </NavLink>
                    <NavLink to="/app/billing" data-testid="nav-billing" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
                        <CreditCard className="w-4 h-4" />
                        <span>{t("nav_billing")}</span>
                    </NavLink>
                    {subscription?.is_admin && (
                        <NavLink to="/app/admin" data-testid="nav-admin" className={({ isActive }) => "sidebar-link" + (isActive ? " active" : "")}>
                            <ShieldCheck className="w-4 h-4" />
                            <span>{t("nav_admin")}</span>
                        </NavLink>
                    )}
                </nav>

                <div className="absolute bottom-6 left-4 right-4">
                    <div className="dotted-divider mb-3" />
                    {subscription && (
                        <div className="mb-3 px-1">
                            <PlanUsageSummary subscription={subscription} t={t} />
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-3">
                        <div className="min-w-0">
                            <div className="text-sm font-semibold text-[#00153f] truncate">{merchant?.name}</div>
                            <div className="text-xs text-gray-500 truncate">{merchant?.email}</div>
                        </div>
                        <LanguagePicker testid="lang-picker-sidebar" />
                    </div>
                    <button onClick={onLogout} data-testid="btn-logout" className="sidebar-link w-full text-left">
                        <LogOut className="w-4 h-4" />
                        <span>{t("nav_logout")}</span>
                    </button>
                </div>
            </aside>
            <main className="px-6 lg:px-10 py-8 lg:py-10 overflow-auto">
                <Outlet context={{ stats, subscription }} />
            </main>
        </div>
    );
}
